import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { CaseCard } from "@/components/site/CaseCard";
import { FaqList } from "@/components/site/FaqList";
import { BlogLinks } from "@/components/site/BlogLinks";
import { StepForm } from "@/components/site/StepForm";
import { SparkleText } from "@/components/site/Sparkle";
import { Seo, breadcrumbLd, faqLd, PROVIDER } from "@/components/site/Seo";
import { SITE_URL, useContent } from "@/lib/api";
import { LANDING_PAGES } from "@/data/landing";
import { PROCESS, WHY, PRICES } from "@/data/landing/common";

const LABEL = { seo: "SEO", local: "Local SEO", ppc: "PPC", meta: "Meta Ads", marketing: "Digital Marketing" };
const SERVICE_TYPE = { seo: "Search engine optimisation", local: "Local search engine optimisation", ppc: "Pay-per-click advertising", meta: "Facebook and Instagram advertising", marketing: "Digital marketing" };

const Ticks = ({ items, dark }) => (
  <ul className="mt-5 space-y-3">
    {items.map((t) => (
      <li key={t} className={`flex items-start gap-3 text-base ${dark ? "text-violet-50" : "text-[#13052A]"}`}>
        <CheckCircle2 className={`mt-0.5 h-5 w-5 shrink-0 ${dark ? "text-[#60A5FA]" : "text-[#7C3AED]"}`} />{t}
      </li>
    ))}
  </ul>
);

const Context = ({ d }) => {
  const side = d.opportunities || d.who;
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="landing-context">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <SectionHead eyebrow={d.kind === "industry" ? "The challenge" : "Local insight"} title={d.context.title} />
          <Reveal className="mt-6 space-y-4 text-base leading-relaxed text-slate-600 md:text-lg">
            {d.context.paras.map((p) => <p key={p}>{p}</p>)}
          </Reveal>
        </div>
        <Reveal delay={0.08} className="self-start rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10" data-testid="landing-who">
          <h3 className="text-2xl font-extrabold leading-tight">{side.title}</h3>
          <Ticks items={side.items} dark />
        </Reveal>
      </div>
    </section>
  );
};

const Services = ({ s }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="landing-services">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="What's included" title={s.title} />
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {s.cards.map((c, i) => (
          <Reveal key={c.title} delay={(i % 3) * 0.06} className="h-full">
            <div className="flex h-full flex-col rounded-[24px] bg-white p-7">
              <h3 className="text-lg font-extrabold text-[#13052A]">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.text}</p>
              {c.link && <Link to={c.link[0]} className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-bold text-[#7C3AED] hover:text-[#581C87]">{c.link[1]} <ArrowUpRight className="h-4 w-4" /></Link>}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Why = ({ d }) => {
  const items = [...WHY, [d.kind === "industry" ? "Sector experience" : "Local knowledge", d.localWhy]];
  return (
    <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="landing-why">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead dark eyebrow="Why Digital AdDoctor" title={d.whyTitle || "Why Work With Digital AdDoctor?"} />
        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {items.map(([t, p], i) => (
            <Reveal key={t} delay={(i % 3) * 0.06}>
              <div className="border-t border-white/15 pt-6">
                <span className="serif-accent text-4xl text-[#C4B5FD]">{i + 1}</span>
                <h3 className="mt-2 text-xl font-extrabold">{t}</h3>
                <p className="mt-3 text-base leading-relaxed text-violet-100">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Results = ({ d }) => {
  const { items } = useContent("case_studies");
  const cases = d.cases.map((s) => items.find((c) => c.slug === s)).filter(Boolean);
  const title = `${d.kind === "industry" ? "" : `${LABEL[d.service]} `}Results and Case Studies`;
  return (
    <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="landing-results">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Real results" title={title} />
        {cases.length > 0 ? (
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {cases.map((c, i) => <Reveal key={c.slug} delay={i * 0.06} className="h-full"><CaseCard c={c} i={i} /></Reveal>)}
          </div>
        ) : d.noCasesNote && <Reveal><p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600" data-testid="landing-no-cases">{d.noCasesNote}</p></Reveal>}
        <Link to="/results" className="btn-ghost mt-8" data-testid="landing-results-link">See all results & case studies <ArrowUpRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
};

const Process = ({ p }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="landing-process">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="The process" title={p.title} />
      <ol className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {p.steps.map(([t, text], i) => (
          <Reveal key={t} delay={(i % 3) * 0.06}>
            <li className="list-none">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#7C3AED] text-sm font-extrabold text-white">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 text-lg font-extrabold text-[#13052A]">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Pricing = ({ d }) => {
  const pr = PRICES[d.service];
  return (
    <section className="bg-white pb-20 sm:pb-28" data-testid="landing-pricing">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-col gap-6 rounded-[28px] border border-violet-200 p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="eyebrow text-[#7C3AED]">{LABEL[d.service]} Packages and Pricing</h2>
            <p className="mt-4 text-3xl font-extrabold text-[#13052A] sm:text-4xl" data-testid="landing-price">{pr.price}</p>
            <div className="mt-4 space-y-2 text-base leading-relaxed text-slate-600">{pr.paras.map((p) => <p key={p}>{p}</p>)}</div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <a href="#enquire" className="btn-accent" data-testid="landing-pricing-cta">Get a tailored quote <ArrowUpRight className="h-4 w-4" /></a>
            <Link to="/pricing" className="btn-ghost" data-testid="landing-all-pricing">View all pricing</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Faqs = ({ faqs }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="landing-faqs">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
      <SectionHead eyebrow="FAQs" title="Frequently Asked Questions" />
      <Reveal><FaqList faqs={faqs} /></Reveal>
    </div>
  </section>
);

const LinkRow = ({ title, links, testid }) => (
  <div data-testid={testid}>
    <h2 className="eyebrow text-[#7C3AED]"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" />{title}</h2>
    <div className="mt-5 flex flex-wrap gap-3">
      {links.map(([to, label]) => <Link key={to} to={to} className="btn-ghost">{label} <ArrowUpRight className="h-4 w-4" /></Link>)}
    </div>
  </div>
);

const Related = ({ d }) => {
  const nearby = (d.nearby || []).filter((s) => LANDING_PAGES[s]).map((s) => [`/${s}`, LANDING_PAGES[s].navTitle]);
  return (
    <section className="bg-white py-20 sm:py-24" data-testid="landing-related">
      <div className="mx-auto max-w-7xl space-y-10 px-5 sm:px-8">
        <LinkRow title="Related services and guides" links={d.links} testid="landing-links" />
        {nearby.length > 0 && <LinkRow title="Nearby areas I also cover" links={nearby} testid="landing-nearby" />}
      </div>
    </section>
  );
};

const Enquire = ({ d, slug }) => (
  <section id="enquire" className="scroll-mt-24 bg-[#F5F0FF] py-20 sm:py-28" data-testid="landing-enquire">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:items-start">
      <div>
        <SectionHead eyebrow="Ready to grow your business?" title={d.final.title} text={d.final.text} />
        <Reveal className="mt-8 space-y-3 text-sm text-slate-600">
          <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#7C3AED]" /> Freya reads every enquiry personally and replies within one to two working days</p>
          <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#7C3AED]" /> No obligation and no hard sell</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#7C3AED]" /> Based in Tunbridge Wells, working across Kent and the UK</p>
        </Reveal>
      </div>
      <Reveal className="rounded-[28px] bg-white p-6 sm:p-10"><StepForm formType={`landing:${slug}`.slice(0, 40)} defaultServices={d.formServices} submitLabel="Send My Enquiry" /></Reveal>
    </div>
  </section>
);

export default function LandingPage({ slug }) {
  const d = { kind: "location", ...LANDING_PAGES[slug] };
  const path = `/${slug}`;
  const crumbs = [["Home", "/"], ...(d.parent ? [d.parent] : []), [d.navTitle, path]];
  const service = {
    "@type": "Service", name: d.h1, serviceType: SERVICE_TYPE[d.service], description: d.metaDescription, provider: PROVIDER, areaServed: d.area, url: `${SITE_URL}${path}`,
    ...(d.audience ? { audience: { "@type": "BusinessAudience", audienceType: d.audience } } : {}),
  };
  return (
    <>
      <Seo raw title={d.metaTitle} description={d.metaDescription} path={path} jsonLd={[service, breadcrumbLd(crumbs), faqLd(d.faqs)]} />
      <PageHero crumbs={crumbs} eyebrow={d.eyebrow} lines={[d.h1]} intro={<SparkleText text={d.tagline} />}
        aside={
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-7 backdrop-blur-xl" data-testid="landing-hero-aside">
            {d.proof && (
              <div className="mb-6 border-b border-white/10 pb-6" data-testid="landing-proof">
                <p className="text-4xl font-extrabold text-white">{d.proof.value}</p>
                <p className="mt-1 text-sm text-violet-200">{d.proof.label}</p>
              </div>
            )}
            <div className="space-y-3 text-sm leading-relaxed text-violet-100">{d.intro.map((p) => <p key={p}>{p}</p>)}</div>
            <p className="mt-5 text-sm text-violet-200">Pricing: <span className="font-bold text-white">{PRICES[d.service].price}</span></p>
          </div>
        }>
        <a href="#enquire" className="btn-accent" data-testid="landing-hero-cta">Let's Talk About Your Business <ArrowUpRight className="h-4 w-4" /></a>
        <Link to="/contact?type=audit" className="btn-ghost-dark" data-testid="landing-hero-audit">Request a Digital Marketing Audit</Link>
      </PageHero>
      <Context d={d} />
      <Services s={d.services} />
      <Why d={d} />
      <Results d={d} />
      <Process p={PROCESS[d.service]} />
      <Pricing d={d} />
      <Faqs faqs={d.faqs} />
      <BlogLinks page={slug} />
      <Related d={d} />
      <Enquire d={d} slug={slug} />
    </>
  );
}
